import { useCanAccessAnalyticsDashboard } from '@/hooks/queries/permissions';
import { useUserStore } from '@/store/use-user-store';
import { useChatStore } from '@/store/useChatStore';
import {
  getLiftHubPath,
  isLiftHubActive,
  isSoulMapConsumerSession,
} from '@/utils/soulmap-auth';
import { cn, Skeleton } from '@nextui-org/react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  BarChart3,
  BookOpen,
  Compass,
  History,
  Home,
  LucideIcon,
} from 'lucide-react';
import { Link, useLocation, useParams } from 'react-router-dom';

interface NavItem {
  path: string;
  icon: LucideIcon;
  label: string;
}

const MobileBottomNav = () => {
  const location = useLocation();
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const userId = useUserStore((state) => state.user?.id);
  // Only hide nav when keyboard is actually open (input is focused), not just when there's text
  const isInputFocused = useChatStore((state) => state.isInputFocused);

  const soulMapConsumer = isSoulMapConsumerSession();
  const { hasPermission: canAccessDashboard, isPending: isNavPending } =
    useCanAccessAnalyticsDashboard();

  const navItems: NavItem[] = [
    {
      path: getLiftHubPath(tenantSlug || ''),
      icon: Home,
      label: 'Hub',
    },
    // While permissions load, skeletons stand in for every item that may show.
    ...(!soulMapConsumer && (isNavPending || canAccessDashboard)
      ? [
          {
            path: `/${tenantSlug}/dashboard`,
            icon: BarChart3,
            label: 'Dashboard',
          },
        ]
      : []),
    ...(!soulMapConsumer
      ? [
          {
            path: `/${tenantSlug}/discover`,
            icon: Compass,
            label: 'Discover',
          },
          {
            path: `/${tenantSlug}/library`,
            icon: BookOpen,
            label: 'Library',
          },
        ]
      : []),
    ...(userId
      ? [
          {
            path: `/${tenantSlug}/practices/users/${userId}`,
            icon: History,
            label: 'Practice History',
          },
        ]
      : []),
  ];

  const isActive = (path: string) => isLiftHubActive(location.pathname, path);

  // Hide navbar only when keyboard is actually open (input is focused)
  const shouldHideNav = isInputFocused;

  return (
    <AnimatePresence>
      {!shouldHideNav && (
        <motion.nav
          initial={{ y: 100 }}
          animate={{ y: 0 }}
          exit={{ y: 100 }}
          transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          className="fixed bottom-0 left-0 right-0 z-[100] md:hidden"
          style={{
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            borderTop: '1px solid rgba(0, 0, 0, 0.06)',
            boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.08)',
            paddingBottom: 'env(safe-area-inset-bottom, 0px)',
            borderRadius: '16px 16px 0 0',
          }}
        >
          {/* Navigation items */}
          <div className="flex items-center justify-around h-14 min-h-14">
            {navItems.map((item) => {
              if (isNavPending) {
                return (
                  <div
                    key={item.path}
                    className="flex min-w-16 items-center justify-center p-3"
                  >
                    <Skeleton className="h-6 w-6 rounded-md" />
                  </div>
                );
              }

              const active = isActive(item.path);
              const IconComponent = item.icon;

              return (
                <Link
                  key={item.path}
                  to={item.path}
                  aria-label={`Navigate to ${item.label}`}
                  className={cn(
                    'relative flex items-center justify-center min-w-16 p-3 transition-all duration-200'
                  )}
                >
                  <motion.div
                    animate={{ scale: active ? 1.05 : 1 }}
                    transition={{ type: 'spring', stiffness: 400, damping: 25 }}
                  >
                    <IconComponent
                      size={24}
                      strokeWidth={active ? 2.5 : 2}
                      className={cn(
                        'transition-colors duration-200',
                        active ? 'text-[#18181b]' : 'text-[#9ca3af]'
                      )}
                    />
                  </motion.div>
                </Link>
              );
            })}
          </div>
        </motion.nav>
      )}
    </AnimatePresence>
  );
};

export default MobileBottomNav;
